import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CustomerService } from '../../../core/services/customer.service';
import { CreateCustomerDto } from '../../../core/models/customer.model';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from '../../../shared/pipes';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

@Component({
  selector: 'app-customer-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, NgxMaskDirective, PhoneFormatPipe, MaxLengthDirective, CharCounterDirective],
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss']
})
export class CustomerFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private customerService = inject(CustomerService);

  form!: FormGroup;
  isEditMode = false;
  customerId: string | null = null;
  isSaving = false;

  // US States for dropdown
  states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  ngOnInit(): void {
    this.initForm();
    this.checkEditMode();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.email]],
      phone: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      notes: ['']
    });
  }

  private checkEditMode(): void {
    this.customerId = this.route.snapshot.paramMap.get('id');
    if (this.customerId) {
      this.isEditMode = true;
      this.loadCustomer(this.customerId);
    }
  }

  private loadCustomer(id: string): void {
    const customer = this.customerService.getCustomerById(id);
    if (customer) {
      this.form.patchValue({
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zip: customer.zip || '',
        notes: customer.notes || ''
      });
    } else {
      // Customer not found, redirect to list
      this.router.navigate(['/customers']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSaving = true;

    const formData = this.form.getRawValue();
    const dto: CreateCustomerDto = {
      name: formData.name,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      zip: formData.zip || undefined,
      notes: formData.notes || undefined
    };

    // Simulate API delay
    setTimeout(() => {
      if (this.isEditMode && this.customerId) {
        this.customerService.updateCustomer(this.customerId, dto);
      } else {
        this.customerService.createCustomer(dto);
      }

      this.isSaving = false;
      this.router.navigate(['/customers']);
    }, 500);
  }

  onCancel(): void {
    this.router.navigate(['/customers']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  openInMaps(): void {
    const address = this.form.get('address')?.value;
    const city = this.form.get('city')?.value;
    const state = this.form.get('state')?.value;
    const zip = this.form.get('zip')?.value;

    const fullAddress = [address, city, state, zip].filter(Boolean).join(', ');
    if (fullAddress) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(url, '_blank');
    }
  }
}
